import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      // Admin goes straight to /admin; everyone else to /dashboard (or the page they came from)
      const target = user?.role === "admin"
        ? "/admin"
        : (location.state?.from || "/dashboard");
      navigate(target);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      let msg;
      if (status === 401) msg = "Wrong email or password. Please try again.";
      else if (status === 429) msg = "Too many failed attempts. Try again in 15 minutes.";
      else if (status === 422) msg = "Please enter a valid email address.";
      else msg = formatApiError(detail) || err.message || "Something went wrong.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Seo title="Login — The Pataudi" description="Login to your The Pataudi account to post and manage listings." />
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-card rounded-card shadow-soft border border-border p-7" data-testid="login-card">
          <h1 className="font-display font-bold text-2xl text-ink">Welcome back</h1>
          <p className="text-sm text-muted-foreground mt-1">Login to manage your listings.</p>

          {error && <div className="mt-4 text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2" data-testid="login-error">{error}</div>}

          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email address" className="mt-1.5 rounded-sm" data-testid="login-email" autoComplete="email" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-accent cursor-pointer hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="rounded-sm pr-10"
                  data-testid="login-password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-ink transition-colors"
                  aria-label={showPwd ? "Hide password" : "Show password"}
                  data-testid="toggle-password-visibility"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90 text-white rounded-sm h-11" data-testid="login-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Login"}
            </Button>
          </form>

          <p className="text-sm text-center text-muted-foreground mt-5">
            Don&apos;t have an account? <Link to="/register" className="text-primary font-medium hover:underline" data-testid="go-register">Register</Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
